import { useDispatch, useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { Tooltip } from "@similarweb/ui-components";

import { useDemoModeDisabled } from "app/pages/sales-intelligence/hooks/demo-mode/useDemoModeDisabled";
import { useFiltersConfigContext } from "app/pages/sales-intelligence/modules/advanced-search-filtering/context/FiltersConfigContext";
import { useFiltersManager } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useFiltersManager";
import leadsSearchTrackingService from "app/pages/sales-intelligence/modules/leads-search/services/tracking/trackingService";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { selectFiltersPanelValue } from "app/pages/sales-intelligence/modules/leads-search-filters";
import type { LeadsSearchFilterInstance } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilterInstance";
import useSavedSearchesList from "app/pages/sales-intelligence/modules/lists/sub-modules/my-list/queries/useSavedSearchsList";
import { saveSearchModalToggledAction } from "app/pages/sales-intelligence/sub-modules/advanced-search/store/action-creators";

import SaveSearchButton from "../../components/SavedSearchButton/SavedSearchButton";
import { useSavedSearchViewModel } from "../../hooks/useSavedSearchViewModel";

const SavedSearchButtonContainer = () => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const { status, filtersList } = useFiltersConfigContext<LeadsSearchFilterInstance>();

    const filterPanelValue = useSelector(selectFiltersPanelValue);

    const filtersManager = useFiltersManager<LeadsSearchAllFiltersTypes>({
        filtersValue: filterPanelValue,
        filtersConfig: filtersList,
        onFiltersUpdate: () => null,
    });

    const { data } = useSavedSearchesList();

    const numberOfActiveSearches = data?.totalCount ?? 0;

    const { dirtyFilterKeys, areFiltersDirty } = useSavedSearchViewModel({
        filtersManager,
    });

    const handleSaveSearchButtonClick = (): void => {
        const dirtyFilterTrackingNames = dirtyFilterKeys.map(
            (key) => filtersManager.getFilterConfig(key)?.trackingName ?? "",
        );

        leadsSearchTrackingService.trackSaveSearchClicked(dirtyFilterTrackingNames, numberOfActiveSearches);

        dispatch(saveSearchModalToggledAction({ isOpen: true }));
    };

    const { isDisabled: isDemoMode, disabledTooltip } = useDemoModeDisabled();
    const isDisabled = status !== "ready" || !areFiltersDirty || isDemoMode;
    // The demo reason wins when active (it is defined only in demo); otherwise the usual save-filters hint.
    const tooltipContent = disabledTooltip ?? translate("si.lead_gen_filters.button.save-filters.tooltip");

    return (
        <Tooltip
            content={tooltipContent}
            enabled={isDisabled}
        >
            <SaveSearchButton
                dataAutomation="filters-panel.save-search-button"
                isDisabled={isDisabled}
                onClick={handleSaveSearchButtonClick}
            />
        </Tooltip>
    );
};

export default SavedSearchButtonContainer;
